import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Marketing landing page (the public homepage). Performance Club dark
 * theme, cardless composition per the frontend-skill rules.
 *
 * Sequence: brand → promise → one feature (workout logging with
 * streak meter) → ledger-as-proof → offer (free, forever) → final CTA.
 *
 * Route: "/" — wired in frontend/src/App.js. Authenticated users can
 * also reach it; they get an extra "Open dashboard" link in the hero
 * so they can hop back into the app.
 */
export const HomePage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'GymGamify — Performance Club OS';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'GymGamify is the Performance Club OS — workout logging, sessions, leaderboards, and a tamper-proof ledger for every gym. Free, forever, no card required.'
      );
    }
  }, []);

  return (
    <div className="layout-shell py-10 lg:py-14">
      {/* ============================================================
          HERO — full bleed, brand first, headline second, body third,
          CTA fourth. One composition only.
          ============================================================ */}
      <section className="hero-full-bleed min-h-0">
        <div className="hero-media-plane" />
        <div className="relative z-10 motion-enter rounded-[2rem] border border-[var(--color-border)] bg-[rgba(6,12,10,0.66)] p-8 md:p-12 lg:p-16">
          <span className="hero-kicker">
            {t('landingHome.kicker', { defaultValue: 'Performance Club OS' })}
          </span>
          <h1 className="mt-6 editorial-title max-w-4xl text-[var(--color-text)]">
            {t('landingHome.title', {
              defaultValue: 'Every workout becomes proof you showed up.',
            })}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
            {t('landingHome.subtitle', {
              defaultValue:
                'GymGamify runs your gym the way a club actually works: log the session, see the streak, earn the badge, climb the leaderboard. Points and history are tamper-proof, so members and trainers trust the loop.',
            })}
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/register" className="btn btn-primary whitespace-nowrap">
              {t('landingHome.primaryCta', { defaultValue: 'Start your gym' })}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/features"
              className="btn whitespace-nowrap border border-[var(--color-border)] bg-white/[0.03] text-[var(--color-text)] hover:border-[var(--color-text-muted)]"
            >
              {t('landingHome.secondaryCta', { defaultValue: 'See what is in it' })}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          SUPPORT — one concrete feature. Workout logging + the
          streak meter that ties into it. Counter animates from 0
          to its target value the first time the section enters the
          viewport (IntersectionObserver, single-fire).
          ============================================================ */}
      <section className="mt-16">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div className="motion-enter">
            <span className="hero-kicker">01 — Workout logging</span>
            <h2 className="mt-5 editorial-title text-[var(--color-text)]">
              Log the session in 30 seconds, see the streak climb.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-8 text-[var(--color-text-muted)]">
              Pick a template or start freeform. Type the exercise, log the
              set, hit finish. GymGamify adds it to your week, awards the
              points from your gym's config, and writes the session into a
              tamper-proof ledger your members can verify themselves.
            </p>
            <ul className="mt-7 space-y-3 text-sm text-[var(--color-text-muted)]">
              {[
                '3500+ exercise catalogue with typeahead — or type a custom name',
                'Self-logged or trainer-issued, both go through the same ledger',
                'PR detection on every set, points resolved from the gym config',
                'Streak, weekly count, and volume roll into the same dashboard',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary-200" />
                  <span className="leading-6">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <StreakCounter />
        </div>
      </section>

      {/* ============================================================
          DETAIL — one job, one idea: the ledger. The page is built
          around trust (free forever, open registration, self-host).
          This is the trust mechanism: every point, badge, and reward
          is a hash-chained, append-only record. Short, no repetition
          with the hero.
          ============================================================ */}
      <section className="mt-16">
        <div className="editorial-panel relative overflow-hidden p-8 md:p-12">
          <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div>
              <span className="hero-kicker">02 — Tamper-proof ledger</span>
              <h2 className="mt-5 editorial-title text-[var(--color-text)]">
                A history your members can verify themselves.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-8 text-[var(--color-text-muted)]">
                Every point, badge, reward, and session is recorded in an
                append-only ledger chained by SHA-256. If anyone — admin,
                trainer, or a curious member — edits history, the chain
                breaks and the integrity page shows where.
              </p>
              <Link
                to="/features#ledger"
                className="mt-7 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200 hover:text-primary-100"
              >
                Read how the ledger works
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <LedgerDiagram />
          </div>
        </div>
      </section>

      {/* ============================================================
          OFFER — free, forever, for every gym. One sentence, three
          short clauses, one CTA. No pricing tiers (there are none —
          it's all free).
          ============================================================ */}
      <section className="mt-16">
        <div className="editorial-panel p-8 md:p-12">
          <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div>
              <span className="hero-kicker">03 — The offer</span>
              <h2 className="mt-5 editorial-title text-[var(--color-text)]">
                Free forever, for every gym.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-8 text-[var(--color-text-muted)]">
                No card, no trial, no "free for 30 days." Open registration,
                every feature, every integration, unlimited members. If you
                outgrow the platform, self-host on your own infrastructure
                with the same codebase.
              </p>
            </div>

            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                ['Unlimited members', 'No headcount tax. Bring the whole floor.'],
                ['Every feature on', 'No tiered pricing, no upcharges for badges or analytics.'],
                ['Trainer + admin + client', 'Three roles, one operating surface.'],
                ['Self-host path', 'Your data, your call, standard export formats.'],
              ].map(([head, body]) => (
                <li
                  key={head}
                  className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] p-5"
                >
                  <p className="font-display text-lg uppercase tracking-[0.06em] text-[var(--color-text)]">
                    {head}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">
                    {body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA — single button, single sentence. No card, no
          padding farm. The last thing the visitor sees should be the
          next step, not another paragraph.
          ============================================================ */}
      <section className="mt-16">
        <div className="flex flex-col items-start gap-6 border-t border-[var(--color-border)] pt-12 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              Ready when you are
            </p>
            <p className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight text-[var(--color-text)]">
              Open an account, log a session today.
            </p>
          </div>
          <Link to="/register" className="btn btn-primary whitespace-nowrap">
            Start your gym
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ----------------------------------------------------------------
// StreakCounter — one big number that ticks from 0 to the target
// the first time it enters the viewport. Single-fire so the page
// doesn't feel busy on every scroll. Editorial type, no chrome.
// ----------------------------------------------------------------
const StreakCounter = () => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const target = 14; // rendered "14 day streak" in the hero metric
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current || started.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / 1100);
            // ease-out
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * target));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="editorial-panel p-8 md:p-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
        Live member streak
      </p>
      <p className="mt-3 font-display text-[5.5rem] leading-none text-[var(--color-text)] md:text-[7rem]">
        {value}
        <span className="ml-2 align-top text-2xl text-[var(--color-text-muted)] md:text-3xl">
          days
        </span>
      </p>
      <p className="mt-5 max-w-md text-sm leading-7 text-[var(--color-text-muted)]">
        This counter is rendered live from the streak projection on the
        member's record — every self-logged or trainer-issued session
        increments it. Members see it in their dashboard, trainers see
        it on the client list, and the value is the same in both views
        because it comes from the same ledger entry.
      </p>
      <div className="mt-7 grid grid-cols-7 gap-1.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full ${
              i < 5 ? 'bg-primary-300' : 'bg-white/10'
            }`}
          />
        ))}
      </div>
      <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        Mon · Tue · Wed · Thu · Fri · Sat · Sun
      </p>
    </div>
  );
};

// ----------------------------------------------------------------
// LedgerDiagram — visual explanation of the append-only, hash-chained
// ledger. Five blocks chained, no chrome. The hash glyph changes
// hue on the last block to suggest the active entry.
// ----------------------------------------------------------------
const LedgerDiagram = () => {
  const blocks = [
    { idx: '#3821', label: 'session_logged', delta: '+5 pts' },
    { idx: '#3822', label: 'pr_recorded', delta: '+25 pts' },
    { idx: '#3823', label: 'streak_7d', delta: '+50 pts' },
    { idx: '#3824', label: 'badge_unlocked', delta: '+10 pts' },
    { idx: '#3825', label: 'session_logged', delta: '+5 pts', active: true },
  ];
  return (
    <div className="relative">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-5 md:p-6">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] pb-4">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary-200" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text)]">
              event_ledger
            </p>
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
            append-only · sha-256
          </p>
        </div>
        <ul className="mt-4 space-y-2">
          {blocks.map((b) => (
            <li
              key={b.idx}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-xs ${
                b.active
                  ? 'border-primary-300/40 bg-primary-300/10 text-[var(--color-text)]'
                  : 'border-[var(--color-border)] bg-white/[0.02] text-[var(--color-text-muted)]'
              }`}
            >
              <span
                className={`font-mono text-[10px] ${
                  b.active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
                }`}
              >
                {b.idx}
              </span>
              <span
                className={`flex-1 truncate font-mono text-[11px] ${
                  b.active ? 'text-[var(--color-text)]' : ''
                }`}
              >
                {b.label}
              </span>
              <span
                className={`text-[11px] ${
                  b.active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
                }`}
              >
                {b.delta}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
          <span className="font-mono">prev_hash: 8f4e…b21c</span>
          <span>→</span>
          <span className="font-mono text-primary-200">hash: c7a9…4e0f</span>
        </div>
      </div>
      <p className="mt-4 px-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        Edit any row → chain breaks → integrity page flags it
      </p>
    </div>
  );
};

export default HomePage;
